import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MarsMart.css";

const storeIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/862/862856.png',
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35]
});

const userIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/1946/1946488.png',
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35]
});

const agentIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/2954/2954884.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40]
});

const STEP_ORDER = ["PENDING", "UNASSIGNED", "PREPARING", "AGENT_REACHING_STORE", "ON_THE_WAY", "REACHED", "DELIVERED"];
const STATUS_CLASS_NAME = {
  PENDING: "mm-status-preparing",
  UNASSIGNED: "mm-status-preparing",
  PREPARING: "mm-status-preparing",
  AGENT_REACHING_STORE: "mm-status-on-the-way", 
  ON_THE_WAY: "mm-status-on-the-way",
  REACHED: "mm-status-on-the-way",
  DELIVERED: "mm-status-delivered",
  CANCELLED: "mm-status-cancelled"
};

export default function MMTracker({ orderId }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Loading...");
  const [eta, setEta] = useState("--");
  const [isMinimized, setIsMinimized] = useState(false);
  const [deliveryCoords, setDeliveryCoords] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  
  const [userLocation, setUserLocation] = useState(null);
  const [storeLocation, setStoreLocation] = useState(null);

  const [deliveryAgentName, setDeliveryAgentName] = useState("--");
  const [deliveryAgentId, setDeliveryAgentId] = useState("--");

  // ✅ 5-Minute Timer States
  const [timeLeft, setTimeLeft] = useState(30);
  const [isCancelling, setIsCancelling] = useState(false);

  // ✅ Timer Logic
  useEffect(() => {
    if (timeLeft > 0 && status === "PENDING") {
      const timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearInterval(timerId);
    }
  }, [timeLeft, status]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    const socket = io(process.env.REACT_APP_BACKEND_URL || "http://localhost:5000");
    if (orderId) {
      socket.emit("join_room", `order_${orderId}`);
    }

    socket.on("location_updated", (coords) => {
      if (Array.isArray(coords)) {
         setDeliveryCoords(coords);
      } else if (coords && coords.lat && coords.lng) {
         setDeliveryCoords([coords.lat, coords.lng]);
      }
    });

    socket.on("order_status_updated", (data) => {
      if (data.status) setStatus(data.status);
      if (data.eta !== undefined) setEta(data.eta);
      if (data.delivery_person_name || data.delivery_agent_name) setDeliveryAgentName(data.delivery_person_name || data.delivery_agent_name);
      if (data.delivery_person_id || data.delivery_agent_id) setDeliveryAgentId(data.delivery_person_id || data.delivery_agent_id);
    });

    return () => socket.disconnect();
  }, [orderId]);

  useEffect(() => {
   const fetchOrderStatus = async () => {
  if (!orderId) return;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/delivery/track/${orderId}`, { credentials: "include" });
    const data = await res.json();
    
    if (data.success && data.order) {
      setStatus(data.order.status || "UNASSIGNED");
      if (data.order.eta_minutes !== undefined) setEta(data.order.actual_time_taken || data.order.eta_minutes);
      if (data.order.delivery_person_name || data.order.delivery_agent_name) setDeliveryAgentName(data.order.delivery_person_name || data.order.delivery_agent_name);
      if (data.order.delivery_person_id || data.order.delivery_agent_id) setDeliveryAgentId(data.order.delivery_person_id || data.order.delivery_agent_id);
      
      // 1. Handle Store Coords
      let finalStoreCoords = null;
      if (data.order.store_coords) {
         finalStoreCoords = typeof data.order.store_coords === 'string' ? data.order.store_coords.split(',').map(Number) : data.order.store_coords;
      } else if (data.order.delivery_agent_coords) {
         finalStoreCoords = typeof data.order.delivery_agent_coords === 'string' ? data.order.delivery_agent_coords.split(',').map(Number) : data.order.delivery_agent_coords;
      }
      
      if (finalStoreCoords && Array.isArray(finalStoreCoords) && finalStoreCoords.length === 2 && !isNaN(finalStoreCoords[0])) {
         setStoreLocation(finalStoreCoords);
      } else {
         setStoreLocation([16.3067, 80.4365]); // Default fallback
      }

      // 2. Handle Agent Coords
      if (data.order.delivery_agent_coords) {
        const raw = data.order.delivery_agent_coords;
        const formattedCoords = typeof raw === 'string' 
            ? raw.split(',').map(parseFloat) 
            : raw;

        if (Array.isArray(formattedCoords) && formattedCoords.length === 2) {
            setDeliveryCoords(formattedCoords);
        }
      }

      // 3. ✅ Handle Dynamic Customer Coords (Replaces Geolocation bug)
      if (data.order.customer_coords) {
         const parts = data.order.customer_coords.split(',');
         setUserLocation([parseFloat(parts[0]), parseFloat(parts[1])]);
      } else {
         setUserLocation(null);
      }

      if (data.order.status === "DELIVERED") {
        setEta(0);
        if (userLocation) setDeliveryCoords(userLocation);
      }

      // ✅ Calculate Timer from DB Creation Time
      const orderTime = new Date(data.order.created_at).getTime();
      const now = new Date().getTime();
      const diffSeconds = Math.floor((now - orderTime) / 1000);
      if (diffSeconds < 30) {
         setTimeLeft(30 - diffSeconds);
      } else {
         setTimeLeft(0);
      }
    }
  } catch (err) {
    console.error("Error fetching order status", err);
  }
};
    fetchOrderStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]); // removed userLocation to avoid loop

  // Sync with Local Storage for My Orders when status is DELIVERED
  useEffect(() => {
    if (status === "DELIVERED" && orderId) {
      try {
        const history = JSON.parse(localStorage.getItem("mm-active-orders") || "[]");
        const updatedHistory = history.map((ord) => {
          if (String(ord.orderId) === String(orderId)) {
            return { ...ord, status: "DELIVERED" };
          }
          return ord;
        });
        localStorage.setItem("mm-active-orders", JSON.stringify(updatedHistory));
      } catch (err) {
        console.error("Error updating local storage", err);
      }
    }
  }, [status, orderId]);

  // Fetch Route from OSRM (2-stage tracking)
  useEffect(() => {
    const fetchRoute = async () => {
      if (["PENDING", "UNASSIGNED", "DELIVERED"].includes(status)) {
        setRoutePath([]);
        return;
      }

      let startLocation = null;
      let endLocation = null;

      if (["PREPARING", "AGENT_REACHING_STORE"].includes(status)) {
        if (!deliveryCoords || !storeLocation) {
          setRoutePath([]);
          return;
        }
        startLocation = deliveryCoords;
        endLocation = storeLocation;
      } else if (["ON_THE_WAY", "REACHED"].includes(status)) {
        const stageTwoStart = deliveryCoords || storeLocation;
        if (!stageTwoStart || !userLocation) {
          setRoutePath([]);
          return;
        }
        startLocation = stageTwoStart;
        endLocation = userLocation;
      } else {
        setRoutePath([]);
        return;
      }

      try {
        const [startLat, startLng] = startLocation;
        const [endLat, endLng] = endLocation;
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${startLng},${startLat};${endLng},${endLat}?geometries=geojson`);
        const data = await res.json();
        if (data.routes && data.routes.length > 0) {
          const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          setRoutePath(coords);
        } else {
          setRoutePath([]);
        }
      } catch (err) {
        console.error("Error fetching route", err);
      }
    };
    fetchRoute();
  }, [deliveryCoords, storeLocation, userLocation, status]);

  const currentRank = STEP_ORDER.indexOf(status) !== -1 ? STEP_ORDER.indexOf(status) + 1 : 1;

  // ✅ Cancel Order Function
  const handleCancelOrder = async () => {
    if (!window.confirm("Are you sure you want to cancel this order? Your refund will be processed in 5-7 working days.")) return;
    setIsCancelling(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5000"}/delivery/cancel/${orderId}`, {
        method: "PUT",
        credentials: "include"
      });
      const data = await res.json();
      if (data.success) {
        setStatus("CANCELLED");
      } else {
        alert(data.message || "Failed to cancel order.");
      }
    } catch (err) {
      console.error(err);
      alert("Error cancelling order.");
    }
    setIsCancelling(false);
  };

  return (
    <div className="mm-page">
      <div className="mm-shell">
        <header className="mm-header">
          <div>
            <p className="mm-kicker">MarsMart Tracker</p>
            <h1 className="mm-title">Order #{orderId || "--"}</h1>
          </div>
          <button
            type="button"
            className="mm-back-btn"
            onClick={() => navigate("/mars-mart")}
          >
            Back to Store
          </button>
        </header>

        {status === "Loading..." ? (
          <div className="mm-loading">Fetching order details...</div>
        ) : status === "CANCELLED" ? (
          <div className="mm-tracker-card" style={{ textAlign: "center", padding: "40px 20px" }}>
            <h2 style={{ color: "#ef4444", fontSize: "28px", marginBottom: "10px" }}>Order Cancelled</h2>
            <p style={{ color: "#64748b", fontSize: "16px" }}>Your order has been cancelled successfully. Refund will be credited to your original payment method in 5-7 working days.</p>
            <button className="mm-place-order-btn" style={{ width: "200px", marginTop: "20px" }} onClick={() => navigate("/mars-mart")}>Continue Shopping</button>
          </div>
        ) : isMinimized ? (
          <div className="mm-mini-tracker" onClick={() => setIsMinimized(false)}>
            <span>🛵 Order in Progress</span>
            <span>{status === "DELIVERED" ? "Delivered successfully" : `ETA: ${eta} mins`}</span>
            <span style={{fontSize: '12px', marginTop: '5px'}}>Click to Maximize ⤢</span>
          </div>
        ) : (
          <section className="mm-tracker-card mm-tracker-card-wrap">
          
          <div className="mm-tracker-head">
            <p className="mm-kicker">Live status</p>
            <span
              className={`mm-status-pill ${STATUS_CLASS_NAME[status] || "mm-status-preparing"}`}
            >
              {status === "UNASSIGNED" ? "Accepted the Order" : status.replace(/_/g, " ")}
            </span>
          </div>

          {/* ✅ CANCELLATION BAR */}
          {status === "PENDING" && timeLeft > 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", padding: "15px", borderRadius: "8px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
               <div>
                 <h4 style={{ margin: "0 0 5px 0", color: "#d97706" }}>Waiting for Confirmation</h4>
                 <p style={{ margin: 0, fontSize: "14px", color: "#b45309" }}>You can cancel your order and get a full refund within: <strong>{formatTime(timeLeft)}</strong></p>
               </div>
               <button 
                 onClick={handleCancelOrder} 
                 disabled={isCancelling}
                 style={{ background: "#ef4444", color: "white", border: "none", padding: "10px 20px", borderRadius: "6px", fontWeight: "bold", cursor: "pointer" }}
               >
                 {isCancelling ? "Cancelling..." : "Cancel Order"}
               </button>
            </div>
          )}

          {(status === "UNASSIGNED" || (status === "PENDING" && timeLeft <= 0)) && (
             <div style={{ textAlign: "center", padding: "2rem 0" }}>
               <h3 style={{ animation: "pulse 2s infinite", color: "#64748b" }}>
                  Looking for a nearby Delivery Partner...
               </h3>
               <div style={{ 
                 width: "40px", height: "40px", 
                 border: "4px solid #f3f3f3", 
                 borderTop: "4px solid #3b82f6", 
                 borderRadius: "50%", 
                 animation: "spin 1s linear infinite",
                 margin: "1rem auto 0" 
               }}></div>
               <style>
                 {`
                   @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                   @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                 `}
               </style>
             </div>
          )}
             <>
                {!(status === "UNASSIGNED" || (status === "PENDING" && timeLeft <= 0)) && (
                  <div className="mm-stepper">
                    {["PREPARING", "AGENT_REACHING_STORE", "ON_THE_WAY", "DELIVERED"].map((step) => {
                      const originalRank = ["PREPARING", "AGENT_REACHING_STORE", "ON_THE_WAY", "DELIVERED"].indexOf(step) + 1;
                      let mappedCurrentRank = currentRank;
                      if (status === "REACHED") mappedCurrentRank = 3; 
                      if (status === "PENDING" || status === "UNASSIGNED") mappedCurrentRank = 0;

                      const isDone = status === "DELIVERED" ? true : originalRank < mappedCurrentRank;
                      const isActive = status !== "DELIVERED" && originalRank === mappedCurrentRank;
                      const stateClass = isDone
                        ? "mm-step-done"
                        : isActive
                        ? "mm-step-active"
                        : "";

                      return (
                        <div key={step} className={`mm-step ${stateClass}`.trim()}>
                          <span className="mm-step-dot" />
                          <span className="mm-step-label">{step.replace(/_/g, " ")}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mm-map-card">
                  {!isMinimized && (
                    <button
                      type="button"
                      className="mm-minimize-btn"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#64748b",
                        fontSize: "12px",
                        textDecoration: "underline",
                        cursor: "pointer",
                        marginBottom: "8px",
                        display: "block",
                        marginLeft: "auto",
                      }}
                      onClick={() => setIsMinimized(true)}
                    >
                      Minimize Map
                    </button>
                  )}
                  <div className="mm-map-view" style={{ height: "300px" }}>
                    <MapContainer
                      key={userLocation ? userLocation[0] : 'loading'}
                      center={deliveryCoords || storeLocation || userLocation || [17.3850, 78.4867]}
                      zoom={14}
                      style={{ height: "100%", width: "100%" }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {storeLocation && (
                        <Marker position={storeLocation} icon={storeIcon}>
                          <Popup>Store 🏪</Popup>
                        </Marker>
                      )}
                      {userLocation && (
                        <Marker position={userLocation} icon={userIcon}>
                          <Popup>User 🏠</Popup>
                        </Marker>
                      )}
                      {deliveryCoords && status !== "DELIVERED" && status !== "PENDING" && status !== "UNASSIGNED" && (
                        <Marker position={deliveryCoords} icon={agentIcon}>
                          <Popup>Delivery Agent 🛵</Popup>
                        </Marker>
                      )}
                      {routePath.length > 0 && status !== "PENDING" && status !== "UNASSIGNED" && (
                        <Polyline positions={routePath} color={["PREPARING", "AGENT_REACHING_STORE"].includes(status) ? "orange" : "blue"} weight={5} />
                      )}
                    </MapContainer>
                  </div>

                  <p className="mm-coords-label">
                    Delivery Partner ID: {(!deliveryAgentId || deliveryAgentId === "--") ? "-" : deliveryAgentId}, Name: {(!deliveryAgentName || deliveryAgentName === "--") ? "-" : deliveryAgentName}
                  </p>
                  <p className="mm-coords-label">
                    {status === "DELIVERED"
                      ? "Delivered successfully."
                      : `Arriving in approximately ${eta === "--" ? "--" : eta} mins.`}
                  </p>

                  {/* ✅ REACHED & OTP SECTION */}
                  {status === "REACHED" && (
                    <div style={{ marginTop: "15px", padding: "15px", backgroundColor: "#f0fdf4", border: "1px dashed #16a34a", borderRadius: "10px", textAlign: "center", animation: "pulse 2s infinite" }}>
                       <p style={{ margin: "0 0 5px 0", color: "#166534", fontWeight: "bold", fontSize: "18px" }}>Agent has Reached Your Location!</p>
                       <p style={{ margin: "0 0 10px 0", fontSize: "14px", color: "#166534" }}>Please share this OTP with the delivery partner.</p>
                       <h2 style={{ margin: "0", fontSize: "40px", letterSpacing: "8px", color: "#15803d" }}>
                         {String(orderId * 73 + 1000).substring(0, 4)}
                       </h2>
                    </div>
                  )}
                </div>
             </>

          </section>
        )}
      </div>
    </div>
  );
}
